import { useEffect, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import type { Listing } from "../../types/company";
import {
  CATEGORIES,
  DRIVER_DOC_TYPES,
  ENERGIES,
  TRANSMISSIONS,
} from "../../data/company";
import { addListing, findListing, listingName, pickupAddress, unitsOf } from "../../lib/company";
import type { ListingDraft } from "../../lib/company";
import { ApiError, assetUrl } from "../../lib/api";
import { createListing, updateListing } from "../../lib/companyApi";
import { useCompany } from "../../lib/companyContext";
import { cn, eur, omit } from "../../lib/format";
import { usePublish } from "../../components/company/usePublish";
import { SelectField, TextAreaField, TextField } from "../../components/ui/FormField";
import AddressField from "../../components/ui/AddressField";
import type { AddressParts } from "../../lib/googlePlaces";
import { ActionButton, BackLink, Hint, PageTitle, StatusBadge } from "../../components/company/ui";
import { label, useT } from "../../i18n";
import { dailyRate, weeklyFromDaily } from "../../lib/rentalPrice";

type Tab = "info" | "photos" | "terms" | "docs" | "units" | "preview";

const TABS: Tab[] = ["info", "photos", "terms", "docs", "units", "preview"];

const NUMBER_FIELDS = ["year", "seats", "mileage", "daily", "weekly", "monthly", "deposit", "minDays", "kmIncluded", "extraKm"] as const;
type NumberField = (typeof NUMBER_FIELDS)[number];

/** Form values: numbers are kept as typed strings until saving. */
type EditorValues = Omit<ListingDraft, NumberField> & Record<NumberField, string>;
type Errors = Partial<Record<keyof EditorValues | "units", string>>;

const EMPTY: EditorValues = {
  brand: "", model: "", version: "", year: "2024", energy: "Hybrid", transmission: "Automatic",
  category: "Sedan", city: "", street: "", postalCode: "", country: "France", seats: "5", colour: "", mileage: "", description: "",
  daily: "", weekly: "", monthly: "", deposit: "", minDays: "1", kmIncluded: "1500", extraKm: "0.12",
  insurance: "", maintenance: "", requiredDocs: ["vtc", "licence", "id", "address"],
  photos: [false, false, false, false, false],
};

const PHOTO_SLOTS = 5;
// Monthly rate suggested from the weekly one: 52 weeks / 12 months
const WEEKS_PER_MONTH = 52 / 12;
const monthlyFrom = (weekly: string) => {
  const n = Number(weekly);
  return n > 0 ? String(Math.round(n * WEEKS_PER_MONTH)) : "";
};
// Same types as the backend (uploadListingPhotos); the extension covers browsers that give no MIME type
const PHOTO_TYPES = "image/jpeg,image/png,image/webp";
const PHOTO_EXTENSIONS = /\.(jpe?g|png|webp)$/i;
const isSupportedPhoto = (file: File) =>
  file.type ? PHOTO_TYPES.split(",").includes(file.type) : PHOTO_EXTENSIONS.test(file.name);
const PHOTO_MAX_BYTES = 10 * 1024 * 1024;

/** Which tab holds each field, to jump to the first error. */
const TAB_OF: Partial<Record<string, Tab>> = {
  daily: "terms", weekly: "terms", monthly: "terms", deposit: "terms", minDays: "terms",
  kmIncluded: "terms", extraKm: "terms", insurance: "terms", maintenance: "terms",
  photos: "photos", requiredDocs: "docs", units: "units",
};

function toValues(l: Listing): EditorValues {
  const rest = omit(l, "publish", "urgentUntil");
  // Listings created before daily prices: start from weekly / 7
  const numbers = Object.fromEntries(
    NUMBER_FIELDS.map((k) => [k, String(k === "daily" ? Math.round(dailyRate(l)) : l[k])]),
  ) as Record<NumberField, string>;
  return { ...rest, ...numbers };
}

/** Keyed by id so switching listings resets the form; waits for the listing to load. */
export default function CompanyListingEditor() {
  const t = useT();
  const { id } = useParams();
  const { state } = useCompany();
  if (id && !findListing(state, id)) {
    const L = t.company.listings;
    return <p className="text-sm text-muted">{state.synced ? L.notFound : L.loading}</p>;
  }
  return <ListingEditor key={id ?? "new"} id={id} />;
}

/** A photo square holds a new file, an uploaded photo's URL, or nothing. */
type PhotoSlotValue = File | string | null;

function ListingEditor({ id }: { id?: string }) {
  const t = useT();
  const ed = t.company.editor;
  const fl = ed.fields;
  const ad = ed.address;
  const ph = t.common.ph;
  const navigate = useNavigate();
  const { state, run, toast } = useCompany();
  const { publish, modal: publishModal } = usePublish();

  const current = id ? findListing(state, id) : undefined;
  const [values, setValues] = useState<EditorValues>(() => (current ? toValues(current) : EMPTY));
  // The monthly rate follows the weekly one until the company types its own
  // (an existing listing keeps the monthly rate it has)
  const [monthlyEdited, setMonthlyEdited] = useState(() => Number(current?.monthly) > 0);
  // Same for the weekly rate, which follows the daily price (× 7)
  const [weeklyEdited, setWeeklyEdited] = useState(() => Number(current?.weekly) > 0);
  const [newUnits, setNewUnits] = useState<{ code: string; plate: string }[]>([]);
  const [tab, setTab] = useState<Tab>("info");
  const [errors, setErrors] = useState<Errors>({});
  const [photoSlots, setPhotoSlots] = useState<PhotoSlotValue[]>(() =>
    Array.from({ length: PHOTO_SLOTS }, (_, i) => current?.photoUrls?.[i] ?? null),
  );
  const [saving, setSaving] = useState(false);
  const [addressQuery, setAddressQuery] = useState("");

  const existingUnits = id ? unitsOf(state, id) : [];
  const set = <K extends keyof EditorValues>(key: K, value: EditorValues[K]) =>
    setValues((v) => ({ ...v, [key]: value }));
  const fillAddress = (a: AddressParts) => {
    setValues((v) => ({
      ...v,
      street: a.street || v.street,
      postalCode: a.postalCode || v.postalCode,
      city: a.city || v.city,
      country: a.country || v.country,
    }));
    setAddressQuery("");
  };
  const savedAddress = pickupAddress(values);
  const prefix = (values.model || "VEH").replace(/[^A-Za-z0-9]/g, "").slice(0, 3).toUpperCase();

  const validate = (): Errors => {
    const e: Errors = {};
    if (!values.brand.trim()) e.brand = t.common.required;
    if (!values.model.trim()) e.model = t.common.required;
    if (!values.city.trim()) e.city = t.common.required;
    for (const k of ["daily", "weekly", "monthly", "deposit"] as const) {
      if (!(Number(values[k]) > 0)) e[k] = ed.amountRequired;
    }
    if (!existingUnits.length && !newUnits.some((u) => u.code.trim())) {
      e.units = ed.unitsRequired;
    }
    return e;
  };

  const showErrors = (e: Errors) => {
    setErrors(e);
    const first = Object.keys(e)[0];
    if (first) setTab(TAB_OF[first] ?? "info");
  };

  // Checked as soon as a photo is picked, not when saving
  const setPhoto = (i: number, file: File | null) => {
    const problem = file && (!isSupportedPhoto(file) ? ed.photoType(file.name) : file.size > PHOTO_MAX_BYTES ? ed.photoTooLarge : "");
    if (problem) {
      setErrors((e) => ({ ...e, photos: problem }));
      return toast.error(problem);
    }
    setErrors((e) => omit(e, "photos"));
    setPhotoSlots((all) => all.map((p, j) => (j === i ? file : p)));
  };

  /**
   * POST (new) or PUT (existing) /company/listings, then publish through the
   * shared flow if asked — it handles the plan limit and unverified companies.
   */
  const save = async (wantsPublish: boolean) => {
    const e = validate();
    if (Object.keys(e).length) return showErrors(e);
    setErrors({});

    const numbers = Object.fromEntries(
      NUMBER_FIELDS.map((k) => [k, Number(values[k]) || 0]),
    ) as Record<NumberField, number>;
    const fields = { ...omit(values, "id", "photos", "photoUrls"), ...numbers };
    const files = photoSlots.filter((p): p is File => p instanceof File);
    const added = newUnits
      .filter((u) => u.code.trim())
      .map((u) => ({ code: u.code.trim(), plate: u.plate.trim() }));

    setSaving(true);
    try {
      const saved = id
        ? await updateListing(id, {
            fields,
            keepPhotos: photoSlots.filter((p): p is string => typeof p === "string"),
            photos: files,
            units: [...existingUnits.map((u) => ({ _id: u.id, code: u.code, plate: u.plate })), ...added],
          })
        : await createListing(fields, files, added);
      run(addListing, saved.listing, saved.units);
      setNewUnits([]);

      const goToListing = () => navigate(`/company/listings/${saved.listing.id}`);
      if (!wantsPublish || saved.listing.publish === "published") {
        toast.success(saved.message);
        return goToListing();
      }
      // On a plan limit the upgrade modal opens here and publishing resumes after it.
      if ((await publish(saved.listing.id, goToListing)) === "failed") goToListing();
    } catch (err) {
      if (err instanceof ApiError && err.errors) showErrors(err.errors as Errors);
      toast.error(err);
    } finally {
      setSaving(false);
    }
  };

  const isPublished = current?.publish === "published";

  // Adding a vehicle is a step-by-step flow: Next checks the current tab and
  // moves on; Save draft / Publish only appear on the last tab.
  // Editing an existing listing keeps the save buttons on every tab.
  const isNew = !id;
  const tabIndex = TABS.indexOf(tab);
  const isLastTab = tabIndex === TABS.length - 1;

  const next = () => {
    const tabErrors = Object.fromEntries(
      Object.entries(validate()).filter(([key]) => (TAB_OF[key] ?? "info") === tab),
    ) as Errors;
    setErrors(tabErrors);
    if (!Object.keys(tabErrors).length) setTab(TABS[tabIndex + 1]);
  };

  return (
    <>
      <BackLink to="/company/listings">{t.company.listings.title}</BackLink>
      <PageTitle className="mt-2.5">
        {current ? ed.editTitle(listingName(current)) : ed.addTitle}
      </PageTitle>

      <div role="tablist" className="mb-5 flex gap-1 overflow-x-auto border-b border-line-strong scrollbar-none">
        {TABS.map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            className={cn(
              "-mb-px h-10 cursor-pointer whitespace-nowrap border-0 border-b-2 bg-transparent px-3.5 text-sm font-medium",
              tab === key ? "border-ink text-ink" : "border-transparent text-muted hover:text-ink",
            )}
          >
            {ed.tabs[key]}
          </button>
        ))}
      </div>

      <div className="rounded-card bg-white p-6">
        {tab === "info" && (
          <div className="grid gap-3.5 sm:grid-cols-2">
            <TextField label={fl.make} placeholder="Toyota" value={values.brand} error={errors.brand} onChange={(e) => set("brand", e.target.value)} />
            <TextField label={fl.model} placeholder="Corolla Touring Sports" value={values.model} error={errors.model} onChange={(e) => set("model", e.target.value)} />
            <TextField label={fl.version} placeholder="Hybrid 140 Design" value={values.version} onChange={(e) => set("version", e.target.value)} />
            <TextField label={fl.year} type="number" placeholder={ph.year} value={values.year} onChange={(e) => set("year", e.target.value)} />
            <SelectField label={fl.fuel} options={ENERGIES} labels={t.enums.energy} value={values.energy} onChange={(e) => set("energy", e.target.value as Listing["energy"])} />
            <SelectField label={fl.gearbox} options={TRANSMISSIONS} labels={t.enums.transmission} value={values.transmission} onChange={(e) => set("transmission", e.target.value as Listing["transmission"])} />
            <SelectField label={fl.category} options={CATEGORIES} labels={t.enums.category} value={values.category} onChange={(e) => set("category", e.target.value)} />
            <TextField label={fl.seats} type="number" placeholder={ph.seats} value={values.seats} onChange={(e) => set("seats", e.target.value)} />
            <TextField label={fl.colour} placeholder={ph.colour} value={values.colour} onChange={(e) => set("colour", e.target.value)} />
            <TextField label={fl.mileage} type="number" placeholder={ph.mileage} value={values.mileage} onChange={(e) => set("mileage", e.target.value)} />
            <TextAreaField full label={fl.description} placeholder={ph.description} value={values.description} onChange={(e) => set("description", e.target.value)} />

            <div className="mt-1.5 border-t border-line pt-4 sm:col-span-2">
              <h3 className="m-0 text-sm font-semibold text-ink">{ad.title}</h3>
              <p className="m-0 mt-1 text-xs text-muted">{ad.hint}</p>
            </div>
            <AddressField
              full
              name="pickup-search"
              label={ad.search}
              placeholder={ad.searchPlaceholder}
              icon={<Search size={14} />}
              value={addressQuery}
              onAddressSelect={fillAddress}
              onChange={(e) => setAddressQuery(e.target.value)}
            />
            <TextField full label={ad.street} placeholder="15 Avenue de la République" autoComplete="street-address" value={values.street} onChange={(e) => set("street", e.target.value)} />
            <TextField label={ad.postalCode} placeholder="75011" autoComplete="postal-code" value={values.postalCode} onChange={(e) => set("postalCode", e.target.value)} />
            <TextField label={ad.city} placeholder="Paris" autoComplete="address-level2" value={values.city} error={errors.city} onChange={(e) => set("city", e.target.value)} />
            <TextField label={ad.country} placeholder="France" autoComplete="country-name" value={values.country} onChange={(e) => set("country", e.target.value)} />
            <div className="flex flex-wrap gap-x-4 gap-y-1 rounded bg-surface px-3.5 py-2.5 text-xs text-muted sm:col-span-2">
              <span>
                {ad.saved} · <span className="text-ink">{savedAddress || "—"}</span>
              </span>
              <span>
                {ad.publicCity} · <span className="text-ink">{values.city.trim() || "—"}</span>
              </span>
            </div>
          </div>
        )}

        {tab === "photos" && (
          <>
            <p className="m-0 mb-3.5 text-sm text-muted">
              {ed.photosHint}
            </p>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-2.5">
              {photoSlots.map((photo, i) => (
                <PhotoSlot
                  key={i}
                  photo={photo}
                  label={ed.addPhoto}
                  removeLabel={t.common.remove}
                  onChange={(file) => setPhoto(i, file)}
                />
              ))}
            </div>
            {errors.photos && <p className="mt-2 mb-0 text-xs text-tone-red-fg">{errors.photos}</p>}
          </>
        )}

        {tab === "terms" && (
          <div className="grid gap-3.5 sm:grid-cols-2">
            <TextField
              label={fl.daily}
              type="number"
              placeholder="45"
              value={values.daily}
              error={errors.daily}
              onChange={(e) => {
                // Weekly (× 7) and monthly (weekly × 52 / 12) follow until the company types its own
                const daily = e.target.value;
                setValues((v) => {
                  const weekly = weeklyEdited ? v.weekly : Number(daily) > 0 ? String(weeklyFromDaily(Number(daily))) : "";
                  return { ...v, daily, weekly, ...(!monthlyEdited && { monthly: monthlyFrom(weekly) }) };
                });
              }}
            />
            <TextField
              label={fl.weekly}
              type="number"
              placeholder="315"
              value={values.weekly}
              error={errors.weekly}
              hint={!weeklyEdited && values.weekly ? ed.weeklyAuto : undefined}
              onChange={(e) => {
                const weekly = e.target.value;
                // Emptying it hands it back to the automatic calculation
                setWeeklyEdited(weekly !== "");
                setValues((v) => ({ ...v, weekly, ...(!monthlyEdited && { monthly: monthlyFrom(weekly) }) }));
              }}
            />
            <TextField
              label={fl.monthly}
              type="number"
              placeholder="1365"
              value={values.monthly}
              error={errors.monthly}
              hint={!monthlyEdited && values.monthly ? ed.monthlyAuto : undefined}
              onChange={(e) => {
                // Emptying it hands it back to the automatic calculation
                setMonthlyEdited(e.target.value !== "");
                set("monthly", e.target.value);
              }}
            />
            <TextField label={fl.deposit} type="number" placeholder="1500" value={values.deposit} error={errors.deposit} onChange={(e) => set("deposit", e.target.value)} />
            <TextField label={fl.kmIncluded} type="number" placeholder={ph.kmIncluded} value={values.kmIncluded} onChange={(e) => set("kmIncluded", e.target.value)} />
            <TextField label={fl.extraKm} type="number" step="0.01" placeholder={ph.extraKm} value={values.extraKm} onChange={(e) => set("extraKm", e.target.value)} />
            <TextAreaField full label={fl.insurance} placeholder={ph.insurance} value={values.insurance} onChange={(e) => set("insurance", e.target.value)} />
            <TextAreaField full label={fl.maintenance} placeholder={ph.maintenance} value={values.maintenance} onChange={(e) => set("maintenance", e.target.value)} />
          </div>
        )}

        {tab === "docs" && (
          <>
            <p className="m-0 mb-3.5 text-sm text-muted">
              {ed.docsHint}
            </p>
            {DRIVER_DOC_TYPES.map((type) => (
              <label key={type} className="flex cursor-pointer items-center gap-2.5 border-b border-[#F0F0F0] py-3 text-sm">
                <input
                  type="checkbox"
                  checked={values.requiredDocs.includes(type)}
                  onChange={() =>
                    set(
                      "requiredDocs",
                      values.requiredDocs.includes(type)
                        ? values.requiredDocs.filter((x) => x !== type)
                        : [...values.requiredDocs, type],
                    )
                  }
                  className="h-4.5 w-4.5 accent-brand"
                />
                {t.company.docTypes[type]}
              </label>
            ))}
          </>
        )}

        {tab === "units" && (
          <>
            <p className="m-0 mb-3.5 text-sm leading-normal text-muted">
              {ed.unitsHint}
            </p>
            {existingUnits.map((u) => (
              <div key={u.id} className="flex items-center gap-2.5 border-b border-[#F0F0F0] py-2.5 text-sm">
                <span className="min-w-22.5 font-mono">{u.code}</span>
                <span className="flex-1 text-muted">{u.plate}</span>
                <StatusBadge kind="unit" status={u.status} />
              </div>
            ))}
            <div className="mt-2.5 grid gap-2">
              {newUnits.map((u, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    value={u.code}
                    placeholder={ed.codePlaceholder}
                    aria-label={ed.code}
                    onChange={(e) => setNewUnits((all) => all.map((x, j) => (j === i ? { ...x, code: e.target.value } : x)))}
                    className="h-10 min-w-0 flex-1 rounded border-0 bg-surface px-3 font-mono text-sm outline-none focus:ring-1 focus:ring-brand"
                  />
                  <input
                    value={u.plate}
                    placeholder={ed.registration}
                    aria-label={ed.registration}
                    onChange={(e) => setNewUnits((all) => all.map((x, j) => (j === i ? { ...x, plate: e.target.value } : x)))}
                    className="h-10 min-w-0 flex-1 rounded border-0 bg-surface px-3 text-sm outline-none focus:ring-1 focus:ring-brand"
                  />
                  <button
                    type="button"
                    aria-label={t.common.remove}
                    onClick={() => setNewUnits((all) => all.filter((_, j) => j !== i))}
                    className="h-10 w-10 cursor-pointer rounded border-0 bg-white text-lg text-faint hover:text-ink"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            {errors.units && <p className="mt-2 mb-0 text-xs text-tone-red-fg">{errors.units}</p>}
            <ActionButton
              className="mt-3 h-9.5 text-sm"
              onClick={() =>
                setNewUnits((all) => [
                  ...all,
                  { code: `${prefix}-${String(existingUnits.length + all.length + 1).padStart(3, "0")}`, plate: "" },
                ])
              }
            >
              {ed.addUnit}
            </ActionButton>
          </>
        )}

        {tab === "preview" && (
          <>
            <p className="m-0 mb-3.5 text-sm text-muted">
              {ed.previewHint}
            </p>
            <div className="max-w-80 overflow-hidden rounded-card border border-line-strong">
              <PreviewPhoto
                photo={photoSlots.find((p) => p !== null) ?? null}
                alt={`${values.brand} ${values.model}`.trim()}
                placeholder={`${t.common.photo} · ${values.brand || fl.make} ${values.model || fl.model}`}
              />
              <div className="grid gap-1.5 px-4.5 py-4">
                <b className="text-lg font-medium">
                  {values.brand || fl.make} {values.model || fl.model}
                </b>
                <span className="text-[13px] text-muted">
                  {label(t.enums.energy, values.energy)} • {label(t.enums.transmission, values.transmission)} ·{" "}
                  {values.city}
                </span>
                <span className="text-[13px]">
                  {ed.from} <b className="text-xl font-semibold">{eur(Number(values.weekly) || 0)}</b>
                  {t.common.perWeek}
                </span>
                <span className="text-[13px] text-muted">
                  {t.card.monthlyAndDeposit(eur(Number(values.monthly) || 0), eur(Number(values.deposit) || 0))}
                </span>
                <span className="text-xs text-tone-green-fg">
                  {ed.fleetCount(existingUnits.length + newUnits.filter((u) => u.code).length)}
                </span>
              </div>
            </div>
          </>
        )}
      </div>

      {current && <Hint className="mt-3 text-muted">{ed.termsHint}</Hint>}

      <div className="mt-4 flex flex-wrap justify-end gap-2.5">
        {isNew && tabIndex > 0 && (
          <ActionButton variant="quiet" size="lg" className="mr-auto" onClick={() => setTab(TABS[tabIndex - 1])}>
            {t.common.back}
          </ActionButton>
        )}
        {isNew && !isLastTab ? (
          <ActionButton variant="primary" size="lg" onClick={next}>
            {ed.next}
          </ActionButton>
        ) : (
          <>
            <ActionButton variant="white" size="lg" disabled={saving} onClick={() => save(false)}>
              {isPublished ? ed.saveChanges : ed.saveDraft}
            </ActionButton>
            {!isPublished && (
              <ActionButton variant="primary" size="lg" disabled={saving} onClick={() => save(true)}>
                {saving ? ed.saving : ed.publishVehicle}
              </ActionButton>
            )}
          </>
        )}
      </div>

      {publishModal}
    </>
  );
}

/** Main photo on the Preview tab: a picked file or an uploaded photo, else a placeholder. */
function PreviewPhoto({ photo, alt, placeholder }: { photo: PhotoSlotValue; alt: string; placeholder: string }) {
  const img = useRef<HTMLImageElement>(null);

  // A picked file gets a temporary URL, freed when it changes (same as PhotoSlot)
  useEffect(() => {
    if (!(photo instanceof File) || !img.current) return;
    const url = URL.createObjectURL(photo);
    img.current.src = url;
    return () => URL.revokeObjectURL(url);
  }, [photo]);

  return (
    <div className="relative flex aspect-16/10 items-center justify-center overflow-hidden bg-linear-to-b from-surface to-[#E2E3E4]">
      {photo instanceof File && <img ref={img} alt={alt} className="absolute inset-0 h-full w-full object-cover" />}
      {typeof photo === "string" && <img src={assetUrl(photo)} alt={alt} className="absolute inset-0 h-full w-full object-cover" />}
      {photo === null && <span className="font-mono text-xs text-faint">{placeholder}</span>}
    </div>
  );
}

interface PhotoSlotProps {
  photo: PhotoSlotValue;
  label: string;
  removeLabel: string;
  onChange: (file: File | null) => void;
}

/** One photo square: click to pick an image, × to remove it. */
function PhotoSlot({ photo, label, removeLabel, onChange }: PhotoSlotProps) {
  const img = useRef<HTMLImageElement>(null);
  const filled = photo !== null;

  // Preview a picked file; the object URL is freed when it changes.
  useEffect(() => {
    if (!(photo instanceof File) || !img.current) return;
    const url = URL.createObjectURL(photo);
    img.current.src = url;
    return () => URL.revokeObjectURL(url);
  }, [photo]);

  return (
    <div className="relative">
      <label
        className={cn(
          "flex aspect-4/3 cursor-pointer items-center justify-center overflow-hidden rounded-md border border-dashed text-xs",
          filled ? "border-brand bg-tone-blue-bg text-brand-link" : "border-[#D0D1D2] bg-[#FAFAFA] text-faint",
        )}
      >
        {photo instanceof File && <img ref={img} alt="" className="h-full w-full object-cover" />}
        {typeof photo === "string" && <img src={assetUrl(photo)} alt="" className="h-full w-full object-cover" />}
        {!filled && label}
        <input
          type="file"
          accept={PHOTO_TYPES}
          className="sr-only"
          onChange={(e) => {
            onChange(e.target.files?.[0] ?? null);
            e.target.value = ""; // allow picking the same file again
          }}
        />
      </label>
      {filled && (
        <button
          type="button"
          aria-label={removeLabel}
          onClick={() => onChange(null)}
          className="absolute top-1.5 right-1.5 flex h-6 w-6 cursor-pointer items-center justify-center rounded-full border-0 bg-white/90 text-ink shadow"
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
}
